import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseOtpTimersOptions {
  /** Seconds until the resend button becomes enabled; also the fallback used by `start()`/`reset()` when called without overrides. */
  resendAfter: number;
  /** Seconds until the code expires; ditto. */
  expiresIn: number;
  /** Seconds the input + verify stay locked after the final failed attempt. Defaults to 60. */
  lockSeconds?: number;
  /** Failed attempts allowed before locking. Defaults to 3. */
  maxAttempts?: number;
}

export interface UseOtpTimersResult {
  /** Seconds left before the resend button re-enables; 0 once it is enabled. */
  secondsToResend: number;
  /** True once `expiresIn` seconds have elapsed since the last `start()`. */
  expired: boolean;
  /** Verification attempts remaining before the lock engages. */
  attemptsLeft: number;
  /** Seconds left on the post-lock countdown; 0 when not locked. */
  lockedFor: number;
  /**
   * (Re)starts the resend/expiry countdowns and resets attempts/lock — call this right after
   * `startOtp` resolves (initial send) and again on resend. Pass overrides to seed the countdowns
   * from that response's own `resendAfterSeconds`/`expiresInSeconds`, since a `setState` made in
   * the same tick (e.g. to store those values in another state variable) is not yet visible to
   * this closure — reading the hook's own `resendAfter`/`expiresIn` props here would be stale.
   */
  start: (overrides?: Partial<Pick<UseOtpTimersOptions, 'resendAfter' | 'expiresIn'>>) => void;
  /** Records one failed verification attempt; the `maxAttempts`-th failure engages the lock countdown. */
  fail: () => void;
  /** Clears all counters back to their configured defaults without re-arming a fresh "just sent" state. */
  reset: () => void;
}

/**
 * Pure timer logic for the OTP step: a resend countdown, an expiry countdown and an
 * attempts-then-lock counter, all driven by a single 1s `setInterval`. Carries no API calls and no
 * copy — `SignIn` reads these numbers/flags and renders the matching `otp.*` string. Kept as a
 * standalone hook (rather than inline state in `SignIn`) specifically so the countdown math can be
 * driven with `vi.useFakeTimers()` in isolation.
 */
export const useOtpTimers = ({
  resendAfter,
  expiresIn,
  lockSeconds = 60,
  maxAttempts = 3,
}: UseOtpTimersOptions): UseOtpTimersResult => {
  const [secondsToResend, setSecondsToResend] = useState(resendAfter);
  const [secondsToExpiry, setSecondsToExpiry] = useState(expiresIn);
  const [attemptsLeft, setAttemptsLeft] = useState(maxAttempts);
  const [lockedFor, setLockedFor] = useState(0);

  // Always-fresh config for the interval/callbacks below, so none of them need `resendAfter` /
  // `expiresIn` / `lockSeconds` / `maxAttempts` in a dependency array — which would otherwise tear
  // down and recreate the running interval on every render that passes a new object/number.
  const configRef = useRef({ resendAfter, expiresIn, lockSeconds, maxAttempts });
  configRef.current = { resendAfter, expiresIn, lockSeconds, maxAttempts };

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsToResend((s) => (s > 0 ? s - 1 : 0));
      setSecondsToExpiry((s) => (s > 0 ? s - 1 : 0));
      setLockedFor((s) => {
        if (s <= 0) return 0;
        const next = s - 1;
        if (next === 0) setAttemptsLeft(configRef.current.maxAttempts);
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const start = useCallback<UseOtpTimersResult['start']>((overrides) => {
    const cfg = configRef.current;
    setSecondsToResend(overrides?.resendAfter ?? cfg.resendAfter);
    setSecondsToExpiry(overrides?.expiresIn ?? cfg.expiresIn);
    setAttemptsLeft(cfg.maxAttempts);
    setLockedFor(0);
  }, []);

  const fail = useCallback(() => {
    setAttemptsLeft((prev) => {
      const next = prev - 1;
      if (next <= 0) {
        setLockedFor(configRef.current.lockSeconds);
        return 0;
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    const cfg = configRef.current;
    setSecondsToResend(cfg.resendAfter);
    setSecondsToExpiry(cfg.expiresIn);
    setAttemptsLeft(cfg.maxAttempts);
    setLockedFor(0);
  }, []);

  return {
    secondsToResend,
    expired: secondsToExpiry <= 0,
    attemptsLeft,
    lockedFor,
    start,
    fail,
    reset,
  };
};
